import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Box,
  Flex,
  Heading,
  useColorModeValue,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

const QuizSurveyDetailTable = ({ contentName, columns, data }) => {
  const { t } = useTranslation();
  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const rowHoverBg = useColorModeValue("gray.100", "gray.750"); // Slightly different hover for sub-table
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const alternateRowBg = useColorModeValue("gray.50", "gray.700"); // Moved hook call here

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange", // Enable column resizing if needed, similar to parent
  });

  if (!columns || columns.length === 0 || !data) {
    return (
      <Text mt={4}>
        {t(
          "ManageEnrollSubjectPage.detail_table_no_data",
          "No data available for this view."
        )}
      </Text>
    );
  }

  return (
    <Box
      mt={6}
      p={4}
      borderWidth="1px"
      borderRadius="md"
      borderColor={borderColor}
      bg={tableBg}
      boxShadow="sm"
    >
      <Heading size="sm" mb={4}>
        {t("ManageEnrollSubjectPage.detail_table_title", "Details for:")}{" "}
        {contentName}
      </Heading>
      <Box overflowX="auto" maxW="100%">
        <Box
          display="inline-block" // Allows table to not stretch full width if content is narrow
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          minWidth="100%" // Ensures it takes at least full width of its container if content is wide
        >
          {/* Table Header */}
          <Flex className="thead" userSelect="none">
            {table.getHeaderGroups()[0].headers.map((header) => (
              <Box
                key={header.id}
                className="th"
                flexGrow={1} // Allow headers to grow
                flexBasis={header.getSize()} // Use Tanstack size
                minW={`${header.column.columnDef.minSize || 80}px`}
                maxW={`${header.column.columnDef.maxSize || 300}px`}
                px={3} // Increased padding slightly
                py={2}
                borderRight="1px solid"
                borderColor={borderColor}
                bg={headerBg}
                fontWeight="semibold"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                position="relative" // For potential resizer, though not implemented here
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
                 {/* Basic Resizer (optional, can be enhanced) */}
                {header.column.getCanResize() && (
                <Box
                  {...{
                    onMouseDown: header.getResizeHandler(),
                    onTouchStart: header.getResizeHandler(),
                  }}
                  className="resizer"
                  sx={{
                    cursor: "col-resize",
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: "100%",
                    width: "5px", // Thinner resizer
                    // bg: 'blue.300', // Optional: make resizer visible for debugging
                    zIndex: 1,
                    touchAction: "none",
                  }}
                />
              )}
              </Box>
            ))}
          </Flex>

          {/* Table Body */}
          {table.getRowModel().rows.map((row, idx) => (
            <Flex
              key={row.id}
              className="tr"
              bg={idx % 2 === 0 ? tableBg : alternateRowBg } // Use pre-calculated color
              _hover={{ bg: rowHoverBg }}
            >
              {row.getVisibleCells().map((cell) => (
                <Box
                  key={cell.id}
                  className="td"
                  flexGrow={1}
                  flexBasis={cell.column.getSize()}
                  minW={`${cell.column.columnDef.minSize || 80}px`}
                  maxW={`${cell.column.columnDef.maxSize || 300}px`}
                  px={3}
                  py={2}
                  borderRight="1px solid"
                  borderColor={borderColor}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              ))}
            </Flex>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default QuizSurveyDetailTable;
